import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericTagPicker, { TagPickerItem, SearchFunction, PrepopulateFunction } from './PickerComponent/GenericTagPicker';
import { CaseResult, searchCaseForNewDocument, searchCasePrepopulate, InsertableArchiveResult } from '../../../services/api/dataServiceOutlookQuickSave';

// Adapter to convert CaseResult to TagPickerItem
const adaptCaseToTagPickerItem = (caseItem: CaseResult): TagPickerItem => ({
    id: caseItem.Recno,
    displayText: `${caseItem.CaseNumber} - ${caseItem.Description}`, // Full text for dropdown and tooltip
    secondaryText: `${caseItem.ResponsiblePerson} - ${caseItem.OrgUnit}`,
    key: caseItem.Recno,
    text: `${caseItem.CaseNumber} - ${caseItem.Description}`, // Show full case info in tag AND dropdown
    imageUrl: null // Cases don't have images, so we'll use the default avatar
});

// Adapter to convert TagPickerItem back to CaseResult
const adaptTagPickerItemToCase = (item: TagPickerItem, cases: CaseResult[]): CaseResult | null => {
    return cases.find(c => c.Recno === item.id) || null;
};

interface CaseTagPickerProps {
    selectedCase?: CaseResult | null;
    selectedCases?: CaseResult[];
    selectedArchive?: InsertableArchiveResult | null;
    isLoading?: boolean;
    error?: string;
    disabled?: boolean;
    multiSelect?: boolean;
    required?: boolean;
    onCaseChange?: (caseItem: CaseResult | null) => void;
    onCasesChange?: (cases: CaseResult[]) => void;
    // QuickSearchTag options
    hideCoin?: boolean;
    // Option to skip prepopulate if it returns empty results
    skipPrepopulate?: boolean;
}

const CaseTagPicker: React.FC<CaseTagPickerProps> = ({
    selectedCase,
    selectedCases = [],
    selectedArchive,
    isLoading = false,
    error,
    disabled = false,
    multiSelect = false,
    required = false,
    onCaseChange,
    onCasesChange,
    hideCoin = false,
    skipPrepopulate = false
}) => {
    const { t } = useTranslation();
    // Keep reference to original cases for conversion back
    const [originalCases, setOriginalCases] = React.useState<CaseResult[]>([]);

    // Search function adapter - STABLE reference
    const searchFunction = React.useMemo<SearchFunction<TagPickerItem, { archive: InsertableArchiveResult | null | undefined }>>(() =>
        async (searchTerm: string, context?: { archive: InsertableArchiveResult | null | undefined }) => {
            if (!context?.archive) {
                return [];
            }

            const archive = context.archive;
            let searchText: string;
            
            // Handle different search scenarios
            if (searchTerm === "%" || searchTerm === "*") {
                searchText = "*"; // Pure wildcard search
            } else {
                searchText = searchTerm + "*"; // Add wildcard to user input
            }

            const results = await searchCaseForNewDocument(searchText, archive.Recno);
            setOriginalCases(results);
            return results.map(adaptCaseToTagPickerItem);
        }, []);

    // Prepopulate function adapter - STABLE reference
    const prepopulateFunction = React.useMemo<PrepopulateFunction<TagPickerItem, { archive: InsertableArchiveResult | null | undefined }>>(() =>
        async () => {
            if (skipPrepopulate) {
                return []; // Skip prepopulate if flag is set
            }

            const results = await searchCasePrepopulate();
            setOriginalCases(results);
            return results.map(adaptCaseToTagPickerItem);
        }, [skipPrepopulate]);

    // Convert selected case(s) to TagPickerItem format
    const selectedItem = selectedCase ? adaptCaseToTagPickerItem(selectedCase) : null;
    const selectedItems = selectedCases.map(adaptCaseToTagPickerItem);

    // Handle single item change
    const handleItemChange = React.useCallback((item: TagPickerItem | null) => {
        if (onCaseChange) {
            const caseItem = item ? adaptTagPickerItemToCase(item, originalCases) : null;
            onCaseChange(caseItem);
        }
    }, [onCaseChange, originalCases]);

    // Handle multiple items change
    const handleItemsChange = React.useCallback((items: TagPickerItem[]) => {
        if (onCasesChange) {
            const caseItems = items
                .map(item => adaptTagPickerItemToCase(item, originalCases))
                .filter(Boolean) as CaseResult[];
            onCasesChange(caseItems);
        }
    }, [onCasesChange, originalCases]);

    const renderCaseOption = React.useCallback((item: TagPickerItem) => {
        // Extract a better name for avatar - use case description part
        const avatarName = item.displayText.includes(' - ') ? 
            item.displayText.split(' - ')[1] || item.displayText :
            item.displayText.split(' ')[0];

        return (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', // Reduced from 8px
                padding: '4px 0px', // Add compact padding
                minHeight: '32px' // Set compact height
            }}>
                {!hideCoin && (
                    <div style={{ 
                        width: '20px', // Reduced from 24px
                        height: '20px', // Reduced from 24px
                        borderRadius: '50%', 
                        background: '#0078d4', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white', 
                        fontSize: '11px', // Reduced from 12px
                        fontWeight: 'bold',
                        flexShrink: 0 // Prevent avatar from shrinking
                    }}>
                        {avatarName.charAt(0).toUpperCase()}
                    </div>
                )}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    gap: '1px', // Minimal gap between primary and secondary text
                    overflow: 'hidden' // Prevent text overflow
                }}>
                    <div style={{ 
                        fontWeight: '600', // Slightly less bold
                        fontSize: '13px', // Reduced from 14px
                        lineHeight: '16px', // Compact line height
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {item.displayText}
                    </div>
                    {item.secondaryText && (
                        <div style={{ 
                            fontSize: '11px', // Reduced from 12px
                            color: '#666', 
                            lineHeight: '14px', // Compact line height
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {item.secondaryText}
                        </div>
                    )}
                </div>
            </div>
        );
    }, [hideCoin]);

    return (
        <GenericTagPicker
            selectedItem={selectedItem}
            selectedItems={selectedItems}
            multiSelect={multiSelect}
            disabled={disabled || !selectedArchive}
            isLoading={isLoading}
            error={error}
            label={t("Case")}
            placeholder={t("Search for cases...")}
            required={required}
            searchFunction={searchFunction}
            prepopulateFunction={prepopulateFunction}
            searchContext={{ archive: selectedArchive }}
            minSearchLength={3}
            searchDebounceMs={300}
            onItemChange={handleItemChange}
            onItemsChange={handleItemsChange}
            renderOption={renderCaseOption}
            hideCoin={hideCoin}
        />
    );
};

export default CaseTagPicker;
